import axios from "axios";
import dayjs from "dayjs";
import { APIResponse } from "../models/NetworkResponse";
import {
  PurchaseTransactionDownloadResult,
  PurchaseTransactionListQuery,
} from "../models/Swagstore/UserPurchaseTransactions";
import { getAPI } from "./Networking";

const formatPurchaseTransactionApiParams = (
  params: PurchaseTransactionListQuery,
): Record<string, string | number> => {
  const { tab_id, shipment_status, start_date, end_date, ...rest } = params;
  const apiParams: Record<string, string | number> = {};

  Object.entries(rest).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      apiParams[key] = value as string | number;
    }
  });

  if (shipment_status?.length) {
    apiParams.shipment_status = shipment_status.join(",");
  }

  if (start_date) {
    apiParams.start_date = dayjs(start_date, "YYYY-MM-DD")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
  }

  if (end_date) {
    apiParams.end_date = dayjs(end_date, "YYYY-MM-DD")
      .add(1, "day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
  }

  return apiParams;
};

const buildPurchaseTransactionQueryString = (
  params: PurchaseTransactionListQuery,
): string => {
  const apiParams = formatPurchaseTransactionApiParams(params);
  const searchParams = new URLSearchParams();

  Object.entries(apiParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, encodeURIComponent(String(value)));
    }
  });

  return searchParams.toString();
};

export default class SwagstorePurchaseTransactionAPI {
  getPurchaseTransactions = async (
    params: PurchaseTransactionListQuery,
  ): Promise<APIResponse> => {
    return getAPI(
      "/v1/swagstore/purchase-transactions",
      formatPurchaseTransactionApiParams(params),
    );
  };

  downloadPurchaseTransactions = async (
    params: PurchaseTransactionListQuery,
  ): Promise<PurchaseTransactionDownloadResult> => {
    const queryString = buildPurchaseTransactionQueryString(params);
    const token = localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || "");
    const baseUrl = process.env.REACT_APP_API_URL || "";

    try {
      const response = await axios({
        baseURL: baseUrl,
        headers: { Authorization: token },
        method: "get",
        url: `/v1/swagstore/purchase-transactions/download?${queryString}`,
        responseType: "blob",
      });

      const contentType = String(
        response.headers["content-type"] ||
          response.headers["Content-Type"] ||
          "",
      );

      if (contentType.includes("application/json")) {
        const text = await (response.data as Blob).text();
        const json = JSON.parse(text) as APIResponse;

        if (!json.success) {
          throw new Error(json.message || "Download failed");
        }

        return {
          type: "email",
          message: json.message || "Report will be sent to your email.",
        };
      }

      const contentDisposition =
        response.headers["content-disposition"] ||
        response.headers["Content-Disposition"] ||
        "";
      const list = contentDisposition.split("=") || [];
      const filename =
        list.length > 1 ? list[1].replace(/"/g, "").trim() : undefined;

      return {
        type: "file",
        data: new Blob([response.data], {
          type: contentType || "text/csv;charset=utf-8",
        }),
        filename,
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        try {
          const errorText = await (error.response.data as Blob).text();
          const errorData = JSON.parse(errorText) as APIResponse;
          throw new Error(errorData.message || "Download failed");
        } catch (parseError) {
          if (
            parseError instanceof Error &&
            parseError.message !== "Download failed"
          ) {
            throw parseError;
          }
        }
      }

      if (error instanceof Error) {
        throw error;
      }

      throw new Error("Download failed");
    }
  };
}
